/**
 * Motor de cubagem.
 *
 * Spec funcional §2.4: a fórmula é atributo do CONTRATO/parceiro comercial, não
 * escolha de tela. A diferença entre fórmulas é a margem. Aqui elas são
 * estratégias plugáveis, e cada uma DECLARA os campos que precisa.
 *
 * Essa declaração é o que impede a UI de divergir da regra (design §6.2): a
 * grade monta as colunas a partir de `camposDaRegra()`, então rótulo, unidade e
 * casas decimais mudam junto com a regra por construção, não por disciplina.
 */
import { Volume } from "./grandezas.js";
import { Decimal, ErroDominio, PI, paraDecimal } from "./numero.js";
export const REGRAS_CUBAGEM = [
    "SERRADA",
    "GEOMETRICA",
    "SMALIAN",
    "HUBER",
    "FRANCON",
    "EMPILHAMENTO",
];
export function ehRegraCubagem(v) {
    return typeof v === "string" && REGRAS_CUBAGEM.includes(v);
}
const CAMPO_PECAS = {
    chave: "pecas",
    rotulo: "Peças",
    unidade: "pç",
    decimais: 0,
    inteiro: true,
};
const CAMPO_TORAS = {
    chave: "pecas",
    rotulo: "Toras",
    unidade: "pç",
    decimais: 0,
    inteiro: true,
};
const CAMPO_COMP = {
    chave: "comp_m",
    rotulo: "Comp. (m)",
    unidade: "m",
    decimais: 2,
};
/** Área seccional de um círculo, a partir do diâmetro em centímetros. */
function areaSeccional(diametroCm) {
    // raio em metros = d_cm / 100 / 2 = d_cm / 200
    const raio = diametroCm.dividedBy(200);
    return PI.times(raio.times(raio));
}
const ESTRATEGIAS = {
    SERRADA: {
        rotulo: "Serrada (geométrica de peça)",
        mede: "espessura, largura e comprimento",
        campos: [
            { chave: "esp_mm", rotulo: "Esp. (mm)", unidade: "mm", decimais: 0 },
            { chave: "larg_mm", rotulo: "Larg. (mm)", unidade: "mm", decimais: 0 },
            CAMPO_COMP,
            CAMPO_PECAS,
        ],
        calcular(m) {
            const esp = m["esp_mm"].dividedBy(1000);
            const larg = m["larg_mm"].dividedBy(1000);
            return esp.times(larg).times(m["comp_m"]).times(m["pecas"]);
        },
    },
    GEOMETRICA: {
        rotulo: "Geométrica / JAS",
        mede: "diâmetro na ponta fina",
        campos: [
            { chave: "d_ponta_cm", rotulo: "Ø ponta (cm)", unidade: "cm", decimais: 1 },
            CAMPO_COMP,
            CAMPO_TORAS,
        ],
        calcular(m) {
            return areaSeccional(m["d_ponta_cm"])
                .times(m["comp_m"])
                .times(m["pecas"]);
        },
    },
    SMALIAN: {
        rotulo: "Smalian",
        mede: "diâmetro nas duas pontas",
        campos: [
            { chave: "d_ponta_cm", rotulo: "Ø ponta (cm)", unidade: "cm", decimais: 1 },
            { chave: "d_base_cm", rotulo: "Ø base (cm)", unidade: "cm", decimais: 1 },
            CAMPO_COMP,
            CAMPO_TORAS,
        ],
        calcular(m) {
            const media = areaSeccional(m["d_ponta_cm"])
                .plus(areaSeccional(m["d_base_cm"]))
                .dividedBy(2);
            return media.times(m["comp_m"]).times(m["pecas"]);
        },
    },
    HUBER: {
        rotulo: "Huber",
        mede: "diâmetro no meio do tronco",
        campos: [
            { chave: "d_meio_cm", rotulo: "Ø meio (cm)", unidade: "cm", decimais: 1 },
            CAMPO_COMP,
            CAMPO_TORAS,
        ],
        calcular(m) {
            return areaSeccional(m["d_meio_cm"])
                .times(m["comp_m"])
                .times(m["pecas"]);
        },
    },
    FRANCON: {
        rotulo: "Francon",
        mede: "circunferência",
        campos: [
            { chave: "circunf_cm", rotulo: "Circunf. (cm)", unidade: "cm", decimais: 1 },
            CAMPO_COMP,
            CAMPO_TORAS,
        ],
        calcular(m) {
            // V = (C/4)² × L — circunferência reduzida a quadrado.
            // Dá exatamente π/4 (≈78,54%) do volume geométrico: mede menos de
            // propósito, favorecendo o comprador. É convenção comercial, não erro.
            const circunfM = m["circunf_cm"].dividedBy(100);
            const lado = circunfM.dividedBy(4);
            return lado.times(lado).times(m["comp_m"]).times(m["pecas"]);
        },
    },
    EMPILHAMENTO: {
        rotulo: "Empilhamento (estéreo)",
        mede: "volume estéreo da pilha",
        campos: [
            { chave: "estereo", rotulo: "Volume (st)", unidade: "st", decimais: 3 },
            {
                chave: "coeficiente",
                rotulo: "Coeficiente",
                unidade: "",
                decimais: 4,
            },
        ],
        calcular(m) {
            return m["estereo"].times(m["coeficiente"]);
        },
    },
};
/**
 * A régua serve para medir esta forma?
 *
 * Tora é redonda: mede-se diâmetro, circunferência ou a pilha em estéreo.
 * Serrada e beneficiada são peças esquadrejadas: espessura × largura ×
 * comprimento. Aplicar circunferência a uma tábua não é imprecisão, é
 * medida sem sentido físico.
 *
 * Sem esta conferência, deixar o seletor de produto na carga anterior fazia
 * o volume de uma espécie entrar como o de outra — e nada avisava, porque
 * cada metade estava correta em separado: a régua vinha do contrato e o
 * produto existia no catálogo.
 */
export function regraServeParaForma(regra, forma) {
    const deTora = regra !== "SERRADA";
    return forma === "TORA" ? deTora : !deTora;
}
/** Mensagem pronta para a recusa — nomeia os dois lados do conflito. */
export function erroDeFormaIncompativel(regra, forma) {
    const e = estrategia(regra);
    return forma === "TORA"
        ? `a régua ${e.rotulo} mede ${e.mede}, e o produto é TORA — escolha uma régua de tora`
        : `a régua ${e.rotulo} mede ${e.mede}, mas o produto é ${forma}: peça serrada mede espessura, largura e comprimento`;
}
/** Colunas que a grade deve exibir para esta regra (design §6.2). */
export function camposDaRegra(regra) {
    return estrategia(regra).campos;
}
/** Rótulo legível — "Francon". */
export function rotuloDaRegra(regra) {
    return estrategia(regra).rotulo;
}
/** O que a regra mede — vai na faixa de contexto ("medindo circunferência"). */
export function oQueMede(regra) {
    return estrategia(regra).mede;
}
function estrategia(regra) {
    const e = ESTRATEGIAS[regra];
    if (!e)
        throw new ErroDominio(`regra de cubagem desconhecida: ${String(regra)}`);
    return e;
}
/**
 * Calcula o volume de uma linha.
 *
 * Valida presença e sanidade de cada campo que a regra declara — campo ausente,
 * negativo ou não numérico estoura com mensagem que nomeia a coluna, para a UI
 * poder marcar a célula certa.
 */
export function cubar(regra, medida) {
    const e = estrategia(regra);
    const valores = {};
    for (const campo of e.campos) {
        const bruto = medida[campo.chave];
        if (bruto === undefined || bruto === null || bruto === "") {
            throw new ErroDominio(`${campo.rotulo}: obrigatório para a regra ${e.rotulo}`);
        }
        const d = paraDecimal(bruto, campo.rotulo);
        if (d.isNegative()) {
            throw new ErroDominio(`${campo.rotulo}: não pode ser negativo`);
        }
        if (campo.inteiro && !d.isInteger()) {
            throw new ErroDominio(`${campo.rotulo}: precisa ser inteiro`);
        }
        valores[campo.chave] = d;
    }
    return Volume.deM3(e.calcular(valores));
}
export function cubarTolerante(regra, medida) {
    try {
        return { ok: true, volume: cubar(regra, medida) };
    }
    catch (e) {
        return {
            ok: false,
            erro: e instanceof Error ? e.message : "erro de cubagem",
        };
    }
}
//# sourceMappingURL=cubagem.js.map